// @ts-strict-ignore
import Space from 'antd/lib/space';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { KickoffActionStatus, ReleaseType, RunwayResponseType } from '../types';
import { runwayColors } from './styles/globalStyles';
import Icon, { BranchesOutlined, InfoCircleOutlined, PullRequestOutlined, FileAddOutlined } from '@ant-design/icons';

// File icon with two "+" signs — used for bump-to-next-version to distinguish it from the single-version bump.
const FileTwoPlusSvg = () => (
    <svg viewBox="64 64 896 896" focusable="false" width="1em" height="1em" fill="currentColor" aria-hidden="true">
        {/* Document outline with folded top-right corner */}
        <path d="M854.6 288.6L639.4 73.4c-6-6-14.1-9.4-22.6-9.4H192c-17.7 0-32 14.3-32 32v832c0 17.7 14.3 32 32 32h640c17.7 0 32-14.3 32-32V311.3c0-8.5-3.4-16.7-9.4-22.7zM790.2 326H602V137.8L790.2 326zm1.8 562H232V136h302v216a42 42 0 0042 42h216v494z" />
        {/* Upper-left "+" */}
        <path d="M396 355 h48 v101 h101 v48 h-101 v101 h-48 v-101 h-101 v-48 h101 z" />
        {/* Lower-right "+" */}
        <path d="M560 585 h48 v101 h101 v48 h-101 v101 h-48 v-101 h-101 v-48 h101 z" />
    </svg>
);
const FileTwoPlusOutlined = (props: React.ComponentProps<typeof Icon>) => (
    <Icon component={FileTwoPlusSvg} {...props} />
);
import { Nullable, ReleaseStepComponentProps, StepRunComponentProps } from '../typescriptHelpers';
import Tooltip from 'antd/lib/tooltip';
import { ReleaseUpdatable } from '../actions/updatable';
import { BaseUpdateReleaseRequest, BumpVersionRequest, tryUpdateRelease } from '../actions/release';
import { StepRunUpdatable } from '../actions/stepRun';
import Popconfirm from 'antd/lib/popconfirm';
import PermissionedActionButton from './HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../helpers/aclHelpers';
import { selectRelease, selectVCSSupportsDirectPushCheck } from '../selectors/selectors';
import { RootState } from '../reducers/reducers';
import Modal from 'antd/lib/modal';
import Text from 'antd/lib/typography/Text';
import BranchCreationCheck from './vcs/BranchCreationCheck';
import { globalMessage } from '../utils/globalMessage';
import { useCallUpdatable, useError, useIsLoading, useKickoffData, useUpdated } from '@/utils/hooks/helpers';

interface KickoffStepFooterProps extends ReleaseStepComponentProps {
    shouldShowEmptyState?: boolean;
    workingBranch: string;
    stepRunComponentProps?: Nullable<StepRunComponentProps>;
}

const KickoffStepFooter: React.FC<KickoffStepFooterProps> = (props) => {
    const { releaseId, shouldShowEmptyState } = props;
    const dispatch = useDispatch();
    const [showCreateReleaseBranchModal, setShowCreateReleaseBranchModal] = React.useState(false);
    const [showPromoteCodePopConfirm, setShowPromoteCodePopConfirm] = React.useState(false);
    const [showBumpVersionPopConfirm, setShowBumpVersionPopConfirm] = React.useState(false);
    const [bumpVersionOnDefaultBranch, setBumpVersionOnDefaultBranch] = React.useState(true);
    const [bumpVersionOnNextVersion, setBumpVersionOnNextVersion] = React.useState(false);

    const [
        kickoffReleaseStepRun,
        kickoffReleaseIsLoadingStepRun,
        kickoffReleaseResponseTypeStepRun,
        kickoffReleaseErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.KickoffRelease, props.stepRunComponentProps);

    const isStepRun = !!props.stepRunComponentProps;
    const kickoffReleaseSuccessStepRun = kickoffReleaseResponseTypeStepRun === RunwayResponseType.Success;

    const createReleaseBranchIsLoadingReleaseStep = useIsLoading(ReleaseUpdatable.CreateReleaseBranch, props.releaseId);
    const createReleaseBranchSuccessReleaseStep = useUpdated(ReleaseUpdatable.CreateReleaseBranch, props.releaseId);
    const createReleaseBranchFailedReleaseStep = useError(ReleaseUpdatable.CreateReleaseBranch, props.releaseId);

    const createReleaseBranchIsLoading =
        isStepRun ? kickoffReleaseIsLoadingStepRun : createReleaseBranchIsLoadingReleaseStep;
    const createReleaseBranchSuccess = isStepRun ? kickoffReleaseSuccessStepRun : createReleaseBranchSuccessReleaseStep;
    const createReleaseBranchFailed = isStepRun ? kickoffReleaseErrorStepRun : createReleaseBranchFailedReleaseStep;

    const promoteBranchIsLoadingReleaseStep = useIsLoading(ReleaseUpdatable.PromoteWorkingBranch, props.releaseId);
    const promoteBranchSuccessReleaseStep = useUpdated(ReleaseUpdatable.PromoteWorkingBranch, props.releaseId);
    const promoteBranchErrorReleaseStep = useError(ReleaseUpdatable.PromoteWorkingBranch, props.releaseId);

    const promoteBranchIsLoading = isStepRun ? kickoffReleaseIsLoadingStepRun : promoteBranchIsLoadingReleaseStep;
    const promoteBranchSuccess = isStepRun ? kickoffReleaseSuccessStepRun : promoteBranchSuccessReleaseStep;
    const promoteBranchError = isStepRun ? kickoffReleaseErrorStepRun : promoteBranchErrorReleaseStep;

    const bumpVersionIsLoading = useIsLoading(ReleaseUpdatable.BumpVersion, props.releaseId);
    const bumpVersionError = useError(ReleaseUpdatable.BumpVersion, props.releaseId);
    const bumpVersionUpdated = useUpdated(ReleaseUpdatable.BumpVersion, props.releaseId);

    const isHotfix = useSelector((state: RootState) => {
        return selectRelease(state, props.releaseId)?.type === ReleaseType.Hotfix;
    });
    const canCheckBranchCreation = useSelector(selectVCSSupportsDirectPushCheck(props.appId, props.orgId));
    const workingBranch = props.workingBranch;
    const kickoffData = useKickoffData(props);

    const releaseBranch = kickoffData?.releaseBranch ?? '';
    const branchExists = kickoffData?.doesReleaseBranchExist ?? false;
    const hotfixBase = kickoffData?.hotfixBranchingBase;
    const base = isHotfix ? hotfixBase : workingBranch;
    const previousReleaseVersion = kickoffData?.previousReleaseVersion;
    const isStaticReleaseBranch = kickoffData?.isStaticReleaseBranch ?? false;
    const canPerformCodePromotion = kickoffData?.canPerformCodePromotion ?? false;
    const codePromotionStatus = kickoffData?.codePromotionStatus;
    const isReleaseBranchSameAsWorkingBranch = releaseBranch === workingBranch;

    const releaseBranchVersionData = kickoffData?.releaseBranchCodeVersionData;
    const workingBranchVersionData = kickoffData?.workingBranchCodeVersionData;
    const nextVersionOnWorkingBranchVersionData = kickoffData?.nextVersionOnWorkingBranchCodeVersionData;

    const defaultVersionData =
        isHotfix || isReleaseBranchSameAsWorkingBranch ? releaseBranchVersionData : workingBranchVersionData;
    const alternateVersionData =
        isHotfix || isReleaseBranchSameAsWorkingBranch ? workingBranchVersionData : releaseBranchVersionData;

    const isHotfixUnableToBranch = isHotfix && !base;
    const createReleaseBranchDescription = `Runway will create '${releaseBranch}'${base ? ` from '${base}'` : ''}.`;

    const createReleaseBranch = () => {
        if (props.stepRunComponentProps) {
            kickoffReleaseStepRun();
            return;
        }

        const request: BaseUpdateReleaseRequest = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId,
        };

        dispatch(tryUpdateRelease(ReleaseUpdatable.CreateReleaseBranch, request, releaseId));
    };

    const bumpVersion = (onBranch: string, targetVersion?: string) => {
        const bumpOnReleaseBranch = onBranch === releaseBranch;

        const request: BumpVersionRequest = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId,
            bumpOnReleaseBranch,
            targetVersion,
        };

        dispatch(tryUpdateRelease(ReleaseUpdatable.BumpVersion, request, releaseId));
    };

    const showBumpVersionConfirm = (useDefaultBranch: boolean) => {
        const branchData = useDefaultBranch ? defaultVersionData : alternateVersionData;
        if (!branchData?.branchName || !branchData.targetCodeVersionExternalString) {
            return;
        }

        Modal.confirm({
            title: `Confirm bump version`,
            content: `Are you sure you want to bump the version on the '${branchData.branchName}' branch?`,
            icon: null,
            onOk() {
                bumpVersion(branchData.branchName);
            },
            okText: `Yes, bump version to ${branchData.targetCodeVersionExternalString}`,
            onCancel() {
                setBumpVersionOnDefaultBranch(true);
            },
        });
    };

    const showBumpVersionWarningConfirm = () => {
        if (!alternateVersionData) {
            return;
        }

        const ruleFailures = alternateVersionData.bumpVersionRuleFailures ?? [];
        const targetCodeVersion = alternateVersionData.targetCodeVersionExternalString || 'the target version';

        Modal.confirm({
            title: 'Are you sure?',
            content: (
                <>
                    <p>You may not want to bump this branch right now, because:</p>
                    <ul>
                        {ruleFailures.length > 0 ?
                            ruleFailures.map((ruleFailure) => <li key={ruleFailure.key}>{ruleFailure.message}</li>)
                        :   <li>One or more bump version requirements are not met.</li>}
                    </ul>
                    <p>You can still attempt to bump the version.</p>
                </>
            ),
            onOk() {
                bumpVersion(alternateVersionData.branchName);
            },
            okText: `Bump version to ${targetCodeVersion} anyway`,
            onCancel() {
                setBumpVersionOnDefaultBranch(true);
            },
        });
    };

    const promoteCodeFromWorkingBranch = () => {
        if (props.stepRunComponentProps) {
            kickoffReleaseStepRun();
            return;
        }

        const request: BaseUpdateReleaseRequest = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId,
        };

        dispatch(tryUpdateRelease(ReleaseUpdatable.PromoteWorkingBranch, request, releaseId));
    };

    React.useEffect(() => {
        if (createReleaseBranchSuccess && !isStaticReleaseBranch) {
            globalMessage.success(`Release branch ${releaseBranch} created!`);

            setShowCreateReleaseBranchModal(false);
        }
    }, [createReleaseBranchSuccess]);

    React.useEffect(() => {
        if (createReleaseBranchFailed && !isStaticReleaseBranch) {
            globalMessage.error(createReleaseBranchFailed);
        }
    }, [createReleaseBranchFailed]);

    React.useEffect(() => {
        if (promoteBranchSuccess && isStaticReleaseBranch) {
            switch (codePromotionStatus) {
                case KickoffActionStatus.Detected:
                    globalMessage.success(`Working branch ${workingBranch} has been promoted to ${releaseBranch}`);
                    break;
                case KickoffActionStatus.Incomplete:
                    globalMessage.info(
                        `A pull request has been opened to merge working branch ${workingBranch} to ${releaseBranch}. Please review to proceed.`,
                    );
                    break;
                case KickoffActionStatus.NotDetected:
                    globalMessage.error(
                        `Could not successfully promote working branch ${workingBranch} to ${releaseBranch}`,
                    );
                    break;
            }

            setShowPromoteCodePopConfirm(false);
        }
    }, [promoteBranchSuccess]);

    React.useEffect(() => {
        if (promoteBranchError && isStaticReleaseBranch) {
            globalMessage.error(promoteBranchError);
        }
    }, [promoteBranchError]);

    React.useEffect(() => {
        if (bumpVersionError) {
            globalMessage.error(bumpVersionError);
        }
    }, [bumpVersionError]);

    React.useEffect(() => {
        if (bumpVersionUpdated) {
            const branchData =
                bumpVersionOnNextVersion ? nextVersionOnWorkingBranchVersionData
                : bumpVersionOnDefaultBranch ? defaultVersionData
                : alternateVersionData;
            if (!branchData?.bumpVersionStatus) {
                setShowBumpVersionPopConfirm(false);
                return;
            }
            switch (branchData.bumpVersionStatus) {
                case KickoffActionStatus.Detected:
                    globalMessage.success(
                        `The version in the ${branchData.branchName} branch has been bumped to ${branchData.targetCodeVersionExternalString}`,
                    );
                    break;
                case KickoffActionStatus.Incomplete:
                    globalMessage.info(
                        `A pull request has been opened to bump the ${branchData.branchName} branch version to ${branchData.targetCodeVersionExternalString}. Please review to proceed.`,
                    );
                    break;
                case KickoffActionStatus.NotDetected:
                    // The bump request itself succeeded (failures surface via
                    // bumpVersionError), so a not-detected status here means the
                    // version change just hasn't been picked up yet.
                    globalMessage.warning(
                        `Version bump submitted, but Runway has not yet detected the version change on the ${branchData.branchName} branch. It may take a moment to be reflected.`,
                    );
                    break;
            }

            setShowBumpVersionPopConfirm(false);
        }
    }, [bumpVersionUpdated]);

    if (!kickoffData) {
        return null;
    }

    const renderBumpVersionCTA = () => {
        if (isReleaseBranchSameAsWorkingBranch || shouldShowEmptyState) {
            return (
                <Popconfirm
                    title={`Are you sure you want to bump the version on the ${defaultVersionData?.branchName} branch?`}
                    open={showBumpVersionPopConfirm}
                    onConfirm={() => bumpVersion(defaultVersionData?.branchName)}
                    okButtonProps={{ loading: bumpVersionIsLoading }}
                    okText="Yes, bump version"
                    onCancel={() => setShowBumpVersionPopConfirm(false)}
                >
                    {!defaultVersionData?.branchName && !shouldShowEmptyState ? null : (
                        <PermissionedActionButton
                            actionType={RunwayUserAction.UABumpVersion}
                            releaseId={props.releaseId}
                            icon={<FileAddOutlined style={{ color: runwayColors.statusGreen }} />}
                            onClick={() => setShowBumpVersionPopConfirm(true)}
                            loading={bumpVersionIsLoading}
                            disabled={!defaultVersionData?.canPerformBumpVersion}
                        >
                            {shouldShowEmptyState ?
                                <>Bump version on main to X.X.X</>
                            :   <>
                                    Bump version on &#39;{defaultVersionData?.branchName}&#39; to{' '}
                                    {defaultVersionData?.targetCodeVersionExternalString}
                                </>
                            }
                        </PermissionedActionButton>
                    )}
                </Popconfirm>
            );
        }

        const onClickBumpAlternate = () => {
            setBumpVersionOnDefaultBranch(false);
            setBumpVersionOnNextVersion(false);
            if (alternateVersionData?.canPerformBumpVersion) {
                showBumpVersionConfirm(false);
            } else {
                showBumpVersionWarningConfirm();
            }
        };

        const showBumpNextVersionWarningConfirm = () => {
            if (!nextVersionOnWorkingBranchVersionData) {
                return;
            }
            const ruleFailures = nextVersionOnWorkingBranchVersionData.bumpVersionRuleFailures ?? [];
            const targetCodeVersion =
                nextVersionOnWorkingBranchVersionData.targetCodeVersionExternalString || 'the target version';
            Modal.confirm({
                title: 'Are you sure?',
                content: (
                    <>
                        <p>You may not want to bump this branch right now, because:</p>
                        <ul>
                            {ruleFailures.length > 0 ?
                                ruleFailures.map((ruleFailure) => <li key={ruleFailure.key}>{ruleFailure.message}</li>)
                            :   <li>One or more bump version requirements are not met.</li>}
                        </ul>
                        {!nextVersionOnWorkingBranchVersionData.targetVersionReleaseExists && (
                            <p>
                                A release does not yet exist for version {targetCodeVersion}, so Runway will create that
                                release as part of this process.
                            </p>
                        )}
                        <p>You can still attempt to bump the version.</p>
                    </>
                ),
                onOk() {
                    bumpVersion(
                        nextVersionOnWorkingBranchVersionData.branchName,
                        nextVersionOnWorkingBranchVersionData.targetCodeVersionInternalString,
                    );
                },
                okText: `Bump version to ${targetCodeVersion} anyway`,
                onCancel() {
                    setBumpVersionOnDefaultBranch(true);
                    setBumpVersionOnNextVersion(false);
                },
            });
        };

        const onClickBumpNextVersion = () => {
            if (
                !nextVersionOnWorkingBranchVersionData?.branchName ||
                !nextVersionOnWorkingBranchVersionData.targetCodeVersionExternalString
            ) {
                return;
            }
            setBumpVersionOnDefaultBranch(false);
            setBumpVersionOnNextVersion(true);
            if (nextVersionOnWorkingBranchVersionData.canPerformBumpVersion) {
                Modal.confirm({
                    title: `Confirm bump version`,
                    content: (
                        <>
                            <p>{`Are you sure you want to bump the version on the '${nextVersionOnWorkingBranchVersionData.branchName}' branch?`}</p>
                            {!nextVersionOnWorkingBranchVersionData.targetVersionReleaseExists && (
                                <p>
                                    A release does not yet exist for version{' '}
                                    {nextVersionOnWorkingBranchVersionData.targetCodeVersionExternalString}, so Runway
                                    will create that as part of this process.
                                </p>
                            )}
                        </>
                    ),
                    icon: null,
                    onOk() {
                        bumpVersion(
                            nextVersionOnWorkingBranchVersionData.branchName,
                            nextVersionOnWorkingBranchVersionData.targetCodeVersionInternalString,
                        );
                    },
                    okText: `Yes, bump version to ${nextVersionOnWorkingBranchVersionData.targetCodeVersionExternalString}`,
                    onCancel() {
                        setBumpVersionOnDefaultBranch(true);
                        setBumpVersionOnNextVersion(false);
                    },
                });
            } else {
                showBumpNextVersionWarningConfirm();
            }
        };

        const menuItems = [
            ...(nextVersionOnWorkingBranchVersionData ?
                [
                    {
                        key: 'bump-next-version',
                        label: `Bump version on '${nextVersionOnWorkingBranchVersionData.branchName}' to ${nextVersionOnWorkingBranchVersionData.targetCodeVersionExternalString}`,
                        icon: <FileTwoPlusOutlined style={{ color: runwayColors.statusGreen }} />,
                        disabled: !nextVersionOnWorkingBranchVersionData.canPerformBumpVersion,
                        onClick: onClickBumpNextVersion,
                    },
                ]
            :   []),
            {
                key: 'bump-alternate',
                label: `Bump version on '${alternateVersionData?.branchName}' to ${alternateVersionData?.targetCodeVersionExternalString}`,
                icon: <FileAddOutlined style={{ color: runwayColors.statusGreen }} />,
                onClick: onClickBumpAlternate,
            },
        ];

        return !defaultVersionData?.branchName && !shouldShowEmptyState ?
                null
            :   <PermissionedActionButton
                    actionType={RunwayUserAction.UABumpVersion}
                    menu={{
                        items: menuItems,
                    }}
                    disabled={shouldShowEmptyState}
                    shouldUseCustomButtonsRender
                    buttonsRender={([leftButton, rightButton]) =>
                        shouldShowEmptyState ?
                            [
                                React.cloneElement(leftButton as React.ReactElement<any, string>, {
                                    disabled: true,
                                }),

                                React.cloneElement(rightButton as React.ReactElement<any, string>, {
                                    disabled: true,
                                }),
                            ]
                        :   [
                                React.cloneElement(leftButton as React.ReactElement<any, string>, {
                                    disabled: !defaultVersionData?.canPerformBumpVersion,
                                    loading: bumpVersionOnDefaultBranch && bumpVersionIsLoading,
                                }),

                                React.cloneElement(rightButton as React.ReactElement<any, string>, {
                                    loading: !bumpVersionOnDefaultBranch && bumpVersionIsLoading,
                                }),
                            ]
                    }
                    trigger={['click']}
                    loading={bumpVersionIsLoading}
                    onClick={() => {
                        setBumpVersionOnDefaultBranch(true);
                        showBumpVersionConfirm(true);
                    }}
                >
                    {shouldShowEmptyState ?
                        <>
                            <>Bump version on main to X.X.X</>
                        </>
                    :   <>
                            Bump version on &#39;{defaultVersionData?.branchName}&#39; to{' '}
                            {defaultVersionData?.targetCodeVersionExternalString}
                        </>
                    }
                </PermissionedActionButton>;
    };

    return (
        <Space size="large" align="center">
            {renderBumpVersionCTA()}
            {!isStaticReleaseBranch && (
                <Space align="center">
                    <PermissionedActionButton
                        actionType={RunwayUserAction.UACreateBranchPromoteCode}
                        releaseId={props.releaseId}
                        icon={<BranchesOutlined style={{ color: runwayColors.statusGreen }} />}
                        onClick={() => setShowCreateReleaseBranchModal(true)}
                        loading={createReleaseBranchIsLoading}
                        disabled={branchExists || isHotfixUnableToBranch || releaseBranch == ''}
                    >
                        {shouldShowEmptyState ?
                            <>Create release branch</>
                        :   <>
                                Create {releaseBranch != '' ? `'` + releaseBranch + `'` : 'unknown'} branch{' '}
                                {base && `from '${base}'`}
                            </>
                        }
                    </PermissionedActionButton>
                    <Modal
                        open={showCreateReleaseBranchModal}
                        title="Create the release branch?"
                        okText="Yes, create release branch"
                        okButtonProps={{ loading: createReleaseBranchIsLoading }}
                        onOk={createReleaseBranch}
                        onCancel={() => setShowCreateReleaseBranchModal(false)}
                    >
                        {canCheckBranchCreation ?
                            <BranchCreationCheck
                                orgId={props.orgId}
                                appId={props.appId}
                                branch={releaseBranch}
                                description={createReleaseBranchDescription}
                            />
                        :   <Text>{createReleaseBranchDescription}</Text>}
                    </Modal>
                    {!branchExists && (
                        <Tooltip
                            title={
                                isHotfixUnableToBranch ?
                                    `You must tag your previous release ${
                                        previousReleaseVersion ? '(' + previousReleaseVersion + ')' : ''
                                    } in order to create this hotfix release branch.`
                                :   'Kick off this release by creating a release branch now.'
                            }
                        >
                            <InfoCircleOutlined style={{ color: runwayColors.info }} />
                        </Tooltip>
                    )}
                </Space>
            )}
            {isStaticReleaseBranch && (
                <Space align="center">
                    <Popconfirm
                        title="Are you sure you want to promote code from your working branch to your release branch?"
                        open={showPromoteCodePopConfirm}
                        onConfirm={promoteCodeFromWorkingBranch}
                        okButtonProps={{ loading: promoteBranchIsLoading }}
                        onCancel={() => setShowPromoteCodePopConfirm(false)}
                        okText="Yes, promote code from working branch"
                    >
                        <PermissionedActionButton
                            actionType={RunwayUserAction.UACreateBranchPromoteCode}
                            releaseId={props.releaseId}
                            icon={<PullRequestOutlined style={{ color: runwayColors.statusGreen }} />}
                            onClick={() => setShowPromoteCodePopConfirm(true)}
                            loading={promoteBranchIsLoading}
                            disabled={
                                !canPerformCodePromotion || codePromotionStatus === KickoffActionStatus.Incomplete
                            }
                        >
                            Promote code from {workingBranch ? "'" + workingBranch + "'" : 'working branch'} →{' '}
                            {releaseBranch ? "'" + releaseBranch + "'" : 'release branch'}
                        </PermissionedActionButton>
                    </Popconfirm>
                    <Tooltip title="Promote the latest code on your working branch to your release branch">
                        <InfoCircleOutlined style={{ color: runwayColors.info }} />
                    </Tooltip>
                </Space>
            )}
        </Space>
    );
};

export default KickoffStepFooter;
